import { API_BASE_URL, BASE_URL } from '@synergy/config';
import { FormattedMessage } from 'react-intl';
import { Locale, Position, PositionType, UserRole } from '@synergy/models';
import { connect } from 'react-redux';
import { getLocalizedString, hasRole } from '@synergy/utils';
import { resolvePath } from 'react-router-dom';
import urljoin from 'url-join';

import { AppState } from '../../../../store/models/app-state';
import { BaseAutocompleteSuggestionType } from '../pool-autocomplete-list';
import { ConfigurationTab } from '../../../../features/configuration/organization/models/configuration-tab';
import { PoolAutocompleteListFooter } from '../pool-autocomplete-list-footer';
import { PoolFormAutocompleteList } from '../../abstract/form-autocomplete-list';
import { PositionsApi } from '../../../api/positions-api';
import { ROUTES } from '../../../../features/sidebar-menu/navigation-drawer/routes';
import { User } from '../../../models/user';
import { localesSelector } from '../../../selectors/locale-selectors';

export interface PartialPosition extends BaseAutocompleteSuggestionType {
    name: string;
}

interface PoolPositionAutocompleteListProps {
    locales: Locale[];
    types: PositionType[];
    user: User;
}

class PoolPositionAutocompleteListBase extends PoolFormAutocompleteList<
    Position,
    PartialPosition,
    PoolPositionAutocompleteListProps
> {
    private actionRoles = [UserRole.Admin, UserRole.SuperAdmin];
    public resetSearch = () => this.resetSearchParams();
    public label = this.props.label || <FormattedMessage id="app.header.position" />;
    public hideId = true;
    private api = new PositionsApi(API_BASE_URL);
    private apiAbortController?: AbortController;
    private searchParams = new URLSearchParams({ limit: '250', types: this.props.types.join(',') });

    public footer = hasRole(this.actionRoles, this.props.user) && (
        <PoolAutocompleteListFooter
            text="app.label.position.add"
            url={urljoin(BASE_URL, resolvePath(ConfigurationTab.Positions, ROUTES.configuration.organization).pathname)}
        />
    );

    private resetSearchParams() {
        this.searchParams = new URLSearchParams({ limit: '250', types: this.props.types.join(',') });
    }

    public componentDidMount() {
        this.setState({ items: [] });
        this.resetSearchParams();
    }

    public getSuggestionValue = (suggestion: PartialPosition) => suggestion && suggestion.name;

    public async handleValueChanged(search: string) {
        this.searchParams.set('search', search);
        this.apiAbortController?.abort();
        this.apiAbortController = new AbortController();
        const response = await this.api.cget(this.searchParams, this.apiAbortController.signal);
        if (!response.success && response.requestCanceled) {
            return;
        }
        const items = response.success ? response.result.items : [];
        this.setState({ items });
    }

    public getPartial = (items: Position[]): PartialPosition[] => {
        return items.map(Position => {
            return {
                id: Position.id,
                name: getLocalizedString(this.props.locales, Position.name),
                dummy: '', // HACK: We need a dummy value to render the list correctly.
            };
        });
    };
}

const mapStateToProps = (state: AppState) => ({
    locales: localesSelector(state),
    user: state.user,
});

export const PoolPositionAutocompleteList = connect(mapStateToProps, {})(PoolPositionAutocompleteListBase);
